/**
 * 焦点陷阱。
 *
 * 只拦 Tab 键，**不拦指针** —— 这两件事必须分开，因为地面站里它们的要求正好相反：
 *   - 键盘用户需要「操作范围有界」：Tab 掉到背后的地图控件上，用户会以为浮层已经关掉了；
 *   - 指针用户需要「实时数据无界」：无蒙版的浮层底下就是姿态与电压，点得到才有意义。
 *
 * 实现方式就是各管一条通道：这里拦 `keydown`，而 realtime 的整层是 `pointer-events: none`。
 */

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button',
  'input',
  'select',
  'textarea',
  'summary',
  'audio[controls]',
  'video[controls]',
  '[contenteditable="true"]',
  '[tabindex]',
].join(',');

/**
 * 是否真的可以接收焦点。
 *
 * 这里**故意不用** `offsetParent !== null` 那类"可见性"判断：jsdom 不实现布局，
 * `offsetParent` 恒为 `null`，于是测试环境会把所有元素都过滤掉、陷阱静默失效 ——
 * 而它恰恰是出错时最难发现的一环。只做与布局无关的判断（`disabled` / `hidden` / `aria-hidden` / `tabindex`）。
 *
 * 特别注意：`aria-disabled` 的元素**必须留在循环里**。它的"为什么不能用"
 *（`aria-describedby`）只有被聚焦时读屏才会念出来，把它踢出焦点循环等于把原因藏起来。
 */
function isUsableFocusTarget(element: HTMLElement): boolean {
  if (element.hasAttribute('disabled')) return false;
  if (element.getAttribute('aria-hidden') === 'true') return false;
  if (element.closest('[hidden]') !== null) return false;
  // tabindex="-1" 是"可编程聚焦"而非"可 Tab 到达"，不该进循环（面板容器自己就是 -1）
  return element.tabIndex >= 0;
}

export function focusableElements(container: HTMLElement): HTMLElement[] {
  const result: HTMLElement[] = [];
  for (const element of container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)) {
    if (isUsableFocusTarget(element)) result.push(element);
  }
  return result;
}

/** 只用到 key / shiftKey / preventDefault，因此对 React 合成事件与本机事件都成立 */
export interface TabKeyEvent {
  readonly key: string;
  readonly shiftKey: boolean;
  preventDefault: () => void;
}

/**
 * 处理一次 Tab：只在**边界**上接管，中间的自由移动交还给浏览器（否则会破坏
 * `tabindex` 的排序语义与第三方组件的键盘行为）。返回是否接管了这次按键。
 */
export function trapTabKey(container: HTMLElement, event: TabKeyEvent): boolean {
  const items = focusableElements(container);
  if (items.length === 0) {
    // 没有任何可聚焦元素时把焦点钉在容器上，否则 Tab 会直接穿到背后
    event.preventDefault();
    container.focus();
    return true;
  }

  const first = items[0];
  const last = items[items.length - 1];
  if (first === undefined || last === undefined) return false;

  const active = typeof document === 'undefined' ? null : document.activeElement;

  if (event.shiftKey) {
    if (active === first || active === container || active === null || !container.contains(active)) {
      event.preventDefault();
      last.focus();
      return true;
    }
    return false;
  }

  // 焦点跑到容器外（例如鼠标点了底下的实时数据）时，Tab 把它拉回浮层内 ——
  // 这正是"指针无界、键盘有界"如何共存的那一步。
  if (active === last || active === null || !container.contains(active)) {
    event.preventDefault();
    first.focus();
    return true;
  }
  return false;
}
